import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostBinding,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { HeroEntity } from '@generated/gql';
import { changeIPFSDomain } from '@helpers/data-helper';
import { DestroyService } from '@services/destroy.service';
import { SubgraphService } from '@services/graph/subgraph.service';
import { Mediator } from '@services/mediator.service';
import { ProviderService } from '@services/provider.service';
import { StorageService } from '@services/storage.service';
import { HeroActions } from '@shared/actions/hero.actions';
import { HeroCardStatsComponent } from '@shared/components/hero-card-stats/hero-card-stats.component';
import { getAvatarByHero, prepareHeroImageUrl } from '@shared/constants/heroes.constant';
import { SACRA_GAME_SELECTED_HERO } from '@shared/constants/storage-keys.constant';
import { filter, finalize, takeUntil } from 'rxjs';

import { ROUTE_WITH_HEADER_HERO_CARD } from './constants/header-hero.constant';

@Component({
  selector: 'app-header-hero',
  standalone: true,
  imports: [HeroCardStatsComponent],
  templateUrl: './header-hero.component.html',
  styleUrl: './header-hero.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderHeroComponent implements OnInit {
  @HostBinding('class.is-hidden')
  get isHidden(): boolean {
    return !(!this.noHeader() && this.isShowCard());
  }

  public readonly noHeader = input<boolean>(false);

  protected readonly heroes = signal<HeroEntity[]>([]);
  protected readonly selectedHero = signal<HeroEntity | null>(null);
  protected readonly heroesAvatar = signal<string | null>(null);
  protected readonly heroImage = signal<string | null>(null);
  public readonly isShowCard = signal(false);
  protected readonly isLoading = signal<boolean>(true);

  private readonly subgraphService = inject(SubgraphService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly providerService = inject(ProviderService);
  private readonly localStorage = inject(StorageService);
  private readonly router = inject(Router);
  private mediator = inject(Mediator);
  private readonly destroy$ = inject(DestroyService);

  private account: string;
  private chainId: number;

  ngOnInit() {
    this.providerService
      .subscribeOnAccountAndNetworkNew$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ account, chainId }) => {
        this.account = account;
        this.chainId = chainId;

        this.loadHeroes();
      });

    this.checkCard(this.router.url);

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
      this.checkCard((event as NavigationEnd).url);
    });

    this.mediator.ofAction(HeroActions.HeroChoose, this.destroy$).subscribe(() => this.selectHero());

    this.providerService.transactionEnded
      .pipe(filter(data => data.isNeedUpdateHero === true || data.isNeedUpdateBalances === true))
      .subscribe(data => {
        if (data.isNeedUpdateHero) {
          this.loadHeroes();
        }
      });
  }

  loadHeroData() {
    if (this.selectedHero()) {
      this.subgraphService
        .hero$(this.selectedHero()!.meta.id, this.selectedHero()!.heroId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(hero => {
          this.selectedHero.set(hero as HeroEntity);
        });
    }
  }

  loadHeroes() {
    this.heroes.set([]);

    this.subgraphService
      .user$(this.account)
      .pipe(
        filter(userInfo => !!userInfo),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(userInfo => {
        const heroes: HeroEntity[] = userInfo!.heroes.map(hero => {
          return hero as HeroEntity;
        });

        this.heroes.set(heroes);

        this.selectHero();
      });
  }

  private initHeroData() {
    this.heroesAvatar.set(getAvatarByHero(this.selectedHero()!));
    this.heroImage.set(changeIPFSDomain(prepareHeroImageUrl(this.selectedHero()!)));
  }

  private checkCard(url: string) {
    const cleanUrl = url.indexOf('?') >= 0 ? url.substring(0, url.indexOf('?')) : url;

    this.isShowCard.set(
      ROUTE_WITH_HEADER_HERO_CARD.some(it => cleanUrl.includes(`/${it}/`) || cleanUrl.includes(`/${it}`)),
    );
  }

  private selectHero() {
    if (this.localStorage.get(SACRA_GAME_SELECTED_HERO)) {
      const key = JSON.parse(this.localStorage.get(SACRA_GAME_SELECTED_HERO));

      const found = this.heroes().find(hero => `${this.account}-${this.chainId}-${hero.id}` === key);

      if (found) {
        this.selectedHero.set(found);
      } else {
        this.selectedHero.set(this.heroes()[0]);
      }
    } else {
      this.selectedHero.set(this.heroes()[0]);
    }

    this.initHeroData();

    this.isLoading.set(false);
  }
}
